import React, { useRef, useState } from 'react';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NavMenu, { MOBILE_TOPBAR_HEIGHT } from './NavMenu';
import TopBar from './TopBar';
import { ambientBackground } from '../../styles/glass';
import { runPageRefresh } from './pullToRefresh';

const THRESHOLD = 70;   // px of (damped) pull that triggers a refresh
const MAX_PULL = 110;

/**
 * Touch only: pulling down at the very top of the page reloads its data (see pullToRefresh). Ignored while a dialog,
 * drawer or menu is open (MUI renders those as .MuiModal-root), on maps (dragging pans them), and when the finger is
 * in an inner scroll area that can still scroll up (e.g. the planner's hour grid).
 */
function canPull(target, main) {
    if (main.scrollTop > 0 || document.querySelector('.MuiModal-root')) return false;
    for (let el = target; el && el !== main; el = el.parentElement) {
        if (el.classList?.contains('leaflet-container')) return false;
        if (el.scrollTop > 0) return false;
    }
    return true;
}

export default function Layout({ children }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const mainRef = useRef(null);
    const start = useRef(null);   // { x, y } while a pull may be in progress
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const onTouchStart = (e) => {
        if (refreshing || e.touches.length !== 1 || !canPull(e.target, mainRef.current)) { start.current = null; return; }
        start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onTouchMove = (e) => {
        if (!start.current) return;
        const dy = e.touches[0].clientY - start.current.y;
        const dx = e.touches[0].clientX - start.current.x;
        // Only a mainly vertical pull downwards, still at the top of the page.
        if (dy <= 0 || Math.abs(dx) > dy || mainRef.current.scrollTop > 0) { if (pull) setPull(0); return; }
        setPull(Math.min(MAX_PULL, dy * 0.5));
    };
    const onTouchEnd = async () => {
        if (!start.current) return;
        start.current = null;
        if (pull < THRESHOLD) { setPull(0); return; }
        setRefreshing(true);
        setPull(THRESHOLD * 0.8);
        // Keep the spinner visible briefly so a fast refresh still reads as one.
        await Promise.all([runPageRefresh().catch(() => {}), new Promise(r => setTimeout(r, 600))]);
        setRefreshing(false);
        setPull(0);
    };

    const top = (isMobile ? MOBILE_TOPBAR_HEIGHT : 0) - 44 + pull;
    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100vh' }}>
            <NavMenu />
            <Box component="main" ref={mainRef} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
                 sx={{
                     flexGrow: 1, minWidth: 0, height: '100vh', overflowY: 'auto', overflowX: 'hidden',
                     // No browser pull-to-refresh (full reload) on top of ours.
                     overscrollBehaviorY: 'contain',
                     background: ambientBackground, backgroundAttachment: 'fixed',
                     pt: isMobile ? `${MOBILE_TOPBAR_HEIGHT}px` : 0,
                     display: 'flex', flexDirection: 'column',
                 }}>
                {(pull > 0 || refreshing) && (
                    <Box aria-hidden sx={{
                        position: 'fixed', left: { xs: '50%', md: 'calc(50% + 130px)' }, top, zIndex: 1250, transform: 'translateX(-50%)',
                        width: 40, height: 40, borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#427898',
                        transition: start.current ? 'none' : 'top 0.2s ease',
                    }}>
                        {refreshing
                            ? <CircularProgress size={20} />
                            : <ArrowDownwardIcon sx={{ fontSize: 20, transition: 'transform 0.15s', transform: pull >= THRESHOLD ? 'rotate(180deg)' : 'none', opacity: Math.min(1, pull / THRESHOLD) }} />}
                    </Box>
                )}
                <TopBar />
                <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: 1400, width: '100%', mx: 'auto', boxSizing: 'border-box' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
}
